import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Star, CheckCircle, XCircle, Loader2,
  AlertTriangle, Activity, Shield, Users, Server, Crown, Key,
  Lock, Zap, Network, GitBranch, BarChart2, TrendingUp,
  ChevronDown, ChevronUp, Circle, Info, Copy, Check,
  FileText, Target, Database, Brain, Sword, Crosshair,
  ShieldAlert, Lightbulb, ArrowRight, Sparkles, Camera,
  Download, Terminal
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
});

// ── Score helpers ─────────────────────────────────────────────────────────────

const scoreColor = (score) => {
  if (score === null || score === undefined) return 'text-gray-400';
  if (score >= 70) return 'text-red-400';
  if (score >= 40) return 'text-orange-400';
  if (score >= 20) return 'text-yellow-400';
  return 'text-green-400';
};

const scoreBg = (score) => {
  if (score === null || score === undefined) return 'bg-gray-500';
  if (score >= 70) return 'bg-red-500';
  if (score >= 40) return 'bg-orange-500';
  if (score >= 20) return 'bg-yellow-500';
  return 'bg-green-500';
};

const riskLabel = (score) => {
  if (score === null || score === undefined) return { label: 'Inconnu', color: 'text-gray-400', bg: 'bg-gray-500/20 border-gray-600' };
  if (score >= 70) return { label: 'CRITIQUE', color: 'text-red-400', bg: 'bg-red-500/20 border-red-500/40' };
  if (score >= 40) return { label: 'ÉLEVÉ', color: 'text-orange-400', bg: 'bg-orange-500/20 border-orange-500/40' };
  if (score >= 20) return { label: 'MOYEN', color: 'text-yellow-400', bg: 'bg-yellow-500/20 border-yellow-500/40' };
  return { label: 'FAIBLE', color: 'text-green-400', bg: 'bg-green-500/20 border-green-500/40' };
};

const severityConfig = {
  Critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500/20 border-red-500/40 text-red-400', dot: 'bg-red-500' },
  High:     { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500/20 border-orange-500/40 text-orange-400', dot: 'bg-orange-500' },
  Medium:   { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', badge: 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400', dot: 'bg-yellow-500' },
  Low:      { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500/20 border-blue-500/40 text-blue-400', dot: 'bg-blue-400' },
  Info:     { bg: 'bg-gray-500/10', border: 'border-gray-700', text: 'text-gray-400', badge: 'bg-gray-500/20 border-gray-600 text-gray-400', dot: 'bg-gray-500' },
};
const getSev = (s) => severityConfig[s] || severityConfig.Info;

const categoryColors = {
  StaleObjects:       { bar: 'bg-blue-500',   text: 'text-blue-400',   icon: <Database className="w-4 h-4 text-blue-400" />,   label: 'Objets périmés' },
  PrivilegedAccounts: { bar: 'bg-red-500',    text: 'text-red-400',    icon: <Crown className="w-4 h-4 text-red-400" />,      label: 'Comptes privilégiés' },
  Trusts:             { bar: 'bg-purple-500', text: 'text-purple-400', icon: <GitBranch className="w-4 h-4 text-purple-400" />, label: 'Relations de confiance' },
  Anomalies:          { bar: 'bg-orange-500', text: 'text-orange-400', icon: <AlertTriangle className="w-4 h-4 text-orange-400" />, label: 'Anomalies' },
};
const getCat = (cat) => categoryColors[cat] || { bar: 'bg-gray-500', text: 'text-gray-400', icon: <Activity className="w-4 h-4 text-gray-400" />, label: cat };

// ── Small helpers ─────────────────────────────────────────────────────────────

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="p-1 text-gray-600 hover:text-gray-300 rounded transition-colors">
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

const KpiCard = ({ icon, label, value, sub, danger, neutral }) => (
  <div className={`bg-brand-black-light rounded-xl border ${danger ? 'border-red-500/30' : 'border-gray-800'} p-4`}>
    <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2 bg-gray-800/60">{icon}</div>
    <div className={`text-3xl font-black ${danger ? 'text-red-400' : 'text-white'}`}>{value ?? '—'}</div>
    <div className="text-sm font-medium text-white mt-0.5">{label}</div>
    {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

// ── Global Score Ring ─────────────────────────────────────────────────────────

const ScoreRing = ({ score }) => {
  const rl = riskLabel(score);
  const pct = Math.min(100, score || 0);
  const r = 40;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const strokeColor = score >= 70 ? '#ef4444' : score >= 40 ? '#f97316' : score >= 20 ? '#eab308' : '#22c55e';

  return (
    <div className="bg-brand-black-light rounded-xl border border-gray-800 p-6">
      <div className="text-sm font-semibold text-gray-400 mb-5">Score de risque global</div>
      <div className="flex items-center gap-8">
        <div className="relative w-28 h-28 flex-shrink-0">
          <svg viewBox="0 0 100 100" className="w-28 h-28 -rotate-90">
            <circle cx="50" cy="50" r={r} fill="none" stroke="#1f2937" strokeWidth="12" />
            <circle cx="50" cy="50" r={r} fill="none"
              stroke={strokeColor} strokeWidth="12"
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.6s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center flex-col">
            <span className={`text-3xl font-black ${scoreColor(score)}`}>{score ?? '—'}</span>
            <span className="text-xs text-gray-500">/100</span>
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-bold ${rl.bg} ${rl.color}`}>
            <AlertTriangle className="w-4 h-4" />
            {rl.label}
          </div>
          <p className="text-xs text-gray-400 leading-relaxed">
            {score >= 70 && 'Le domaine présente des vulnérabilités critiques nécessitant une action immédiate.'}
            {score >= 40 && score < 70 && 'Des risques significatifs ont été identifiés dans le domaine Active Directory.'}
            {score >= 20 && score < 40 && 'Quelques points d\'amélioration à traiter en priorité moyenne.'}
            {(score || 0) < 20 && 'Le domaine présente un faible niveau de risque. Maintenir les bonnes pratiques.'}
          </p>
        </div>
      </div>
    </div>
  );
};

// ── Category Scores ───────────────────────────────────────────────────────────

const CategoryScores = ({ scores, byCategory }) => (
  <div className="bg-brand-black-light rounded-xl border border-gray-800 p-5">
    <div className="text-sm font-semibold text-gray-400 mb-4">Scores par catégorie</div>
    <div className="space-y-4">
      {Object.entries(scores).map(([cat, score]) => {
        const cfg = getCat(cat);
        const indicators = byCategory[cat] || [];
        return (
          <div key={cat}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                {cfg.icon}
                <span className="text-sm font-medium text-gray-300">{cfg.label}</span>
                {indicators.length > 0 && (
                  <span className="text-xs text-gray-500">({indicators.length} indicateur{indicators.length > 1 ? 's' : ''})</span>
                )}
              </div>
              <span className={`text-lg font-black ${scoreColor(score)}`}>{score}</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div className={`h-full ${cfg.bar} rounded-full transition-all duration-700`}
                style={{ width: `${Math.min(100, score || 0)}%` }} />
            </div>
          </div>
        );
      })}
      {Object.keys(scores).length === 0 && (
        <p className="text-center text-gray-500 text-sm py-4">Aucun score calculé</p>
      )}
    </div>
  </div>
);

// ── Indicator Card ────────────────────────────────────────────────────────────

const IndicatorCard = ({ indicator, index }) => {
  const [expanded, setExpanded] = useState(false);
  const sc = getSev(indicator.severity);
  const cfg = getCat(indicator.category);

  return (
    <div className={`rounded-xl border overflow-hidden ${sc.bg} ${sc.border}`}>
      <div className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02]"
        onClick={() => setExpanded(!expanded)}>
        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border ${sc.badge}`}>
          <span className="text-xs font-black">{index + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">{indicator.title}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border font-bold uppercase ${sc.badge}`}>
              {indicator.severity}
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-800/60 px-1.5 py-0.5 rounded border border-gray-700">
              {cfg.icon && React.cloneElement(cfg.icon, { className: 'w-3 h-3' })}
              {cfg.label}
            </span>
            {indicator.id && (
              <span className="text-xs font-mono text-gray-600">{indicator.id}</span>
            )}
          </div>
          {indicator.description && !expanded && (
            <p className="text-xs text-gray-400 mt-1 line-clamp-1">{indicator.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {indicator.matched_count > 0 && (
            <span className="text-xs font-bold bg-gray-700 text-white px-2 py-0.5 rounded-full">
              {indicator.matched_count}
            </span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-3 border-t border-gray-800/40 space-y-3">
          {indicator.description && (
            <div>
              <span className="text-xs font-semibold text-gray-500">Description</span>
              <p className="mt-1 text-sm text-gray-300 leading-relaxed">{indicator.description}</p>
            </div>
          )}
          {indicator.matched_count > 0 && (
            <div className={`flex items-center gap-2 p-2.5 rounded-lg border ${sc.bg} ${sc.border}`}>
              <AlertTriangle className={`w-4 h-4 ${sc.text} flex-shrink-0`} />
              <span className={`text-sm font-semibold ${sc.text}`}>
                {indicator.matched_count} objet{indicator.matched_count > 1 ? 's' : ''} concerné{indicator.matched_count > 1 ? 's' : ''}
              </span>
            </div>
          )}
          {indicator.solution && (
            <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                <span className="text-xs text-green-400 font-semibold">Solution recommandée</span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{indicator.solution}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Action Plan ───────────────────────────────────────────────────────────────

const ActionPlan = ({ indicators }) => {
  const sevOrder = { Critical: 0, High: 1, Medium: 2, Low: 3, Info: 4 };
  const sorted = [...indicators].sort((a, b) =>
    (sevOrder[a.severity] ?? 4) - (sevOrder[b.severity] ?? 4)
  );
  const critical = sorted.filter(i => i.severity === 'Critical');
  const high = sorted.filter(i => i.severity === 'High');
  const medium = sorted.filter(i => ['Medium', 'Low', 'Info'].includes(i.severity));

  const Section = ({ title, items, color, icon, horizon }) => {
    if (items.length === 0) return null;
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-3">
          {icon}
          <span className={`text-sm font-bold ${color}`}>{title}</span>
          <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full border border-gray-700">{horizon}</span>
        </div>
        {items.map((item, i) => {
          const sc = getSev(item.severity);
          return (
            <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${sc.bg} ${sc.border}`}>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-black ${sc.text}`}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white">{item.title}</div>
                {item.solution && <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{item.solution}</p>}
                {item.matched_count > 0 && (
                  <span className="inline-block mt-1 text-xs text-gray-500">{item.matched_count} objet{item.matched_count > 1 ? 's' : ''} concerné{item.matched_count > 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (indicators.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-12 h-12 text-green-500/40 mx-auto mb-3" />
        <p className="text-gray-400 font-medium">Aucune action requise</p>
        <p className="text-gray-600 text-sm mt-1">Le domaine ne présente pas d'indicateurs de risque.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Section title="Actions immédiates" items={critical} color="text-red-400"
        icon={<AlertTriangle className="w-4 h-4 text-red-400" />} horizon="< 30 jours" />
      <Section title="Actions prioritaires" items={high} color="text-orange-400"
        icon={<TrendingUp className="w-4 h-4 text-orange-400" />} horizon="30 – 90 jours" />
      <Section title="Améliorations recommandées" items={medium} color="text-yellow-400"
        icon={<Info className="w-4 h-4 text-yellow-400" />} horizon="90 – 180 jours" />
    </div>
  );
};

// ── AI Section Renderer ───────────────────────────────────────────────────────

const AISectionBlock = ({ title, icon, content, color = 'purple-400', collapsible = true }) => {
  const [open, setOpen] = useState(true);
  const lines = (content || '').split('\n').filter(l => l.trim());

  return (
    <div className={`rounded-xl border overflow-hidden bg-brand-black-light border-${color}/20`}>
      <div
        className={`flex items-center justify-between px-5 py-3.5 ${collapsible ? 'cursor-pointer hover:bg-white/[0.02]' : ''} border-b border-${color}/10`}
        onClick={collapsible ? () => setOpen(!open) : undefined}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg bg-${color}/10 flex items-center justify-center flex-shrink-0`}>
            {icon}
          </div>
          <span className="font-bold text-white text-sm">{title}</span>
        </div>
        {collapsible && (open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />)}
      </div>
      {open && (
        <div className="px-5 py-4 space-y-2">
          {lines.map((line, i) => {
            const trimmed = line.trim();
            if (/^\d+\.\s/.test(trimmed)) {
              return (
                <div key={i} className="flex items-start gap-2.5">
                  <span className={`text-xs font-black text-${color} w-5 flex-shrink-0 mt-0.5`}>{trimmed.match(/^\d+/)[0]}.</span>
                  <p className="text-sm text-gray-300 leading-relaxed">{trimmed.replace(/^\d+\.\s/, '')}</p>
                </div>
              );
            }
            if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
              return (
                <div key={i} className="flex items-start gap-2.5">
                  <ArrowRight className={`w-3.5 h-3.5 text-${color} flex-shrink-0 mt-0.5`} />
                  <p className="text-sm text-gray-300 leading-relaxed">{trimmed.replace(/^[-•]\s/, '')}</p>
                </div>
              );
            }
            if (trimmed.startsWith('`') || line.startsWith('    ')) {
              return (
                <div key={i} className="rounded-lg bg-black/60 border border-gray-800 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-900/60 border-b border-gray-800">
                    <Terminal className="w-3 h-3 text-gray-500" />
                    <span className="text-xs text-gray-500">commande</span>
                  </div>
                  <div className="px-3 py-2 font-mono text-xs text-green-400 whitespace-pre-wrap">
                    {trimmed.replace(/^`+|`+$/g, '')}
                  </div>
                </div>
              );
            }
            if (trimmed.startsWith('###') || trimmed.startsWith('**')) {
              return (
                <p key={i} className={`text-sm font-bold text-${color} mt-3 mb-1`}>
                  {trimmed.replace(/^#+\s*|^\*\*|\*\*$/g, '')}
                </p>
              );
            }
            if (trimmed.startsWith('##')) {
              return <p key={i} className="text-sm font-bold text-white mt-2">{trimmed.replace(/^#+\s*/, '')}</p>;
            }
            if (trimmed) {
              return <p key={i} className="text-sm text-gray-300 leading-relaxed">{trimmed}</p>;
            }
            return <div key={i} className="h-2" />;
          })}
          {lines.length === 0 && (
            <p className="text-sm text-gray-500 italic">Aucune information disponible.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Indicator Captures ────────────────────────────────────────────────────────

const IndicatorCaptures = ({ indicators, domain, dc_ip, scores, stats }) => {
  const downloadAll = () => {
    const header = [
      '=' .repeat(70),
      `RAPPORT DE PREUVES — AUDIT AD`,
      `Domaine : ${domain}  |  DC : ${dc_ip}`,
      `Date    : ${new Date().toLocaleString()}`,
      '='.repeat(70),
      '',
    ].join('\n');

    const scoresSection = [
      'SCORES PAR CATÉGORIE',
      '-'.repeat(40),
      ...Object.entries(scores).map(([k, v]) => `  ${k}: ${v}/100`),
      '',
    ].join('\n');

    const statsSection = [
      'STATISTIQUES AD',
      '-'.repeat(40),
      ...Object.entries(stats).map(([k, v]) => `  ${k}: ${v}`),
      '',
    ].join('\n');

    const indsSection = indicators.map((ind, i) => [
      `${'='.repeat(60)}`,
      `[${i + 1}] [${(ind.severity || 'Info').toUpperCase()}] [${ind.category}] ${ind.title}`,
      `ID: ${ind.id || 'N/A'}  |  Objets concernés: ${ind.matched_count || 0}`,
      '-'.repeat(60),
      `Description: ${ind.description || 'N/A'}`,
      `Solution: ${ind.solution || 'N/A'}`,
      '',
    ].join('\n')).join('\n');

    const blob = new Blob([header + scoresSection + statsSection + indsSection], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-ad_preuves_${domain}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const criticalAndHigh = indicators.filter(i => ['Critical', 'High'].includes(i.severity));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-400">
          <Camera className="w-4 h-4" />
          <span className="text-sm font-medium">
            {indicators.length} indicateur{indicators.length > 1 ? 's' : ''} — {criticalAndHigh.length} critique{criticalAndHigh.length > 1 ? 's' : ''}/élevé{criticalAndHigh.length > 1 ? 's' : ''}
          </span>
        </div>
        <button onClick={downloadAll}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-300 rounded-lg text-xs hover:bg-gray-700 border border-gray-700 transition-colors">
          <Download className="w-3.5 h-3.5" /> Télécharger le rapport de preuves (.txt)
        </button>
      </div>

      {/* Scores capture */}
      {Object.keys(scores).length > 0 && (
        <div className="rounded-xl border border-purple-500/30 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-purple-500/10 border-b border-purple-500/20">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-bold text-white">Scores Audit AD — {domain}</span>
            </div>
            <CopyBtn text={Object.entries(scores).map(([k, v]) => `${k}: ${v}/100`).join('\n')} />
          </div>
          <div className="bg-black/90">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#1a1a1a] border-b border-gray-800">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
              </div>
              <span className="text-xs text-gray-500 font-mono">audit-ad@scan ~ scores</span>
            </div>
            <div className="p-4 font-mono text-xs text-gray-300 whitespace-pre-wrap leading-relaxed">
              <span className="text-green-400">$</span> <span className="text-blue-300"># Score Global Audit AD: {scores.global_score ?? '—'}/100</span>
              {'\n'}{Object.entries(scores).map(([k, v]) => `  ${k.padEnd(25)} ${v}/100`).join('\n')}
            </div>
          </div>
        </div>
      )}

      {/* Indicators captures */}
      {indicators.map((ind, i) => {
        const sc = getSev(ind.severity);
        const cfg = getCat(ind.category);
        const evidenceText = [
          `Title    : ${ind.title}`,
          `Severity : ${ind.severity}`,
          `Category : ${ind.category}`,
          `ID       : ${ind.id || 'N/A'}`,
          `Matched  : ${ind.matched_count || 0} objects`,
          ind.description ? `Desc     : ${ind.description}` : null,
          ind.solution ? `Solution : ${ind.solution}` : null,
        ].filter(Boolean).join('\n');

        return (
          <div key={i} className={`rounded-xl border overflow-hidden ${sc.border}`}>
            <div className={`flex items-center justify-between px-4 py-2.5 ${sc.bg} border-b ${sc.border}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${sc.dot}`} />
                <span className="text-sm font-bold text-white">{ind.title}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded border font-bold uppercase ${sc.badge}`}>{ind.severity}</span>
                <span className="text-xs text-gray-500">{cfg.label}</span>
              </div>
              <div className="flex items-center gap-2">
                {ind.matched_count > 0 && (
                  <span className="text-xs font-bold bg-gray-700 text-white px-2 py-0.5 rounded-full">
                    {ind.matched_count} obj.
                  </span>
                )}
                <span className="text-xs text-gray-500">#{i + 1}</span>
                <CopyBtn text={evidenceText} />
              </div>
            </div>
            <div className="bg-black/90">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#1a1a1a] border-b border-gray-800">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                </div>
                <span className="text-xs text-gray-500 font-mono">audit-ad@scan ~ indicator-{i + 1}</span>
              </div>
              <div className="p-4 font-mono text-xs text-gray-300 whitespace-pre-wrap leading-relaxed">
                <span className="text-green-400">$</span> <span className="text-blue-300"># Indicator evidence</span>
                {'\n'}{evidenceText}
              </div>
            </div>
          </div>
        );
      })}

      {indicators.length === 0 && (
        <div className="text-center py-12">
          <Camera className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Aucun indicateur disponible</p>
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const PingCastleDetail = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [polling, setPolling] = useState(false);
  const [indFilter, setIndFilter] = useState('all');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSubTab, setAiSubTab] = useState('analysis');
  const aiTriggeredRef = useRef(false);

  const fetchScan = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/v1/pingcastle/scans/${scanId}`, {
        headers: getAuthHeaders(),
      });
      if (!r.ok) { setError(`Audit introuvable (HTTP ${r.status})`); return; }
      const d = await r.json();
      setData(d);
      setPolling(d.status === 'running');
    } catch (e) {
      setError(`Erreur réseau : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [scanId]);

  useEffect(() => { fetchScan(); }, [fetchScan]);
  useEffect(() => {
    if (!polling) return;
    const t = setInterval(fetchScan, 4000);
    return () => clearInterval(t);
  }, [polling, fetchScan]);

  // Charger analyse depuis DB ou auto-déclencher quand scan terminé
  useEffect(() => {
    if (!data) return;
    if (data.ai_analysis && !aiAnalysis) {
      setAiAnalysis(data.ai_analysis);
      aiTriggeredRef.current = true;
      return;
    }
    if (data.status === 'completed' && !aiAnalysis && !loadingAI && !aiTriggeredRef.current) {
      aiTriggeredRef.current = true;
      generateAIAnalysis();
    }
  }, [data?.status, data?.ai_analysis]); // eslint-disable-line

  const generateAIAnalysis = async () => {
    setLoadingAI(true);
    setAiError(null);
    try {
      const r = await fetch(`${API_BASE}/api/v1/pingcastle/scans/${scanId}/analyze`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const result = await r.json();
      setAiAnalysis(result);
    } catch (e) {
      setAiError(`Erreur lors de la génération : ${e.message}`);
    } finally {
      setLoadingAI(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-brand-black flex items-center justify-center">
      <div className="flex items-center gap-3 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
        <span>Chargement de l'audit AD…</span>
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-brand-black flex items-center justify-center">
      <div className="text-center">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-white font-semibold mb-1">Audit introuvable</p>
        <p className="text-gray-400 text-sm mb-4">{error}</p>
        <button onClick={() => navigate('/ad')}
          className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 transition-colors">
          Retour à la page AD
        </button>
      </div>
    </div>
  );

  const result = data.result || {};
  const scores = result.scores || {};
  const indicators = result.indicators || [];
  const stats = result.stats || {};
  const globalScore = result.global_score;
  const rl = riskLabel(globalScore);

  const byCategory = indicators.reduce((acc, ind) => {
    acc[ind.category] = acc[ind.category] || [];
    acc[ind.category].push(ind);
    return acc;
  }, {});

  const sevCounts = { Critical: 0, High: 0, Medium: 0, Low: 0, Info: 0 };
  indicators.forEach(ind => { if (sevCounts[ind.severity] !== undefined) sevCounts[ind.severity]++; });

  const sevOrder = { Critical: 0, High: 1, Medium: 2, Low: 3, Info: 4 };
  const sortedIndicators = [...indicators].sort((a, b) =>
    (sevOrder[a.severity] ?? 4) - (sevOrder[b.severity] ?? 4)
  );
  const filteredIndicators = indFilter === 'all' ? sortedIndicators
    : sortedIndicators.filter(i => i.severity === indFilter);

  const tabs = [
    { id: 'overview',    label: 'Synthèse',                    icon: <Activity className="w-4 h-4" /> },
    { id: 'indicators',  label: `Indicateurs (${indicators.length})`, icon: <AlertTriangle className="w-4 h-4" />, badge: sevCounts.Critical > 0 ? sevCounts.Critical : null },
    { id: 'stats',       label: 'Statistiques AD',             icon: <BarChart2 className="w-4 h-4" /> },
    { id: 'actions',     label: 'Plan d\'action',              icon: <FileText className="w-4 h-4" /> },
    { id: 'ai',          label: 'Analyse IA',                  icon: <Brain className="w-4 h-4" />, highlight: true },
  ];

  const aiSections = aiAnalysis?.sections || {};
  const sectionDefs = [
    { key: 'SYNTHÈSE DES DÉCOUVERTES',    label: 'Synthèse des découvertes',              icon: <Lightbulb className="w-4 h-4 text-yellow-400" />,   color: 'yellow-400' },
    { key: "CHEMINS D'ATTAQUE IDENTIFIÉS", label: "Chemins d'attaque identifiés",          icon: <Crosshair className="w-4 h-4 text-orange-400" />,   color: 'orange-400' },
    { key: 'GUIDE DE COMPROMISSION AD',   label: 'Guide de compromission Active Directory', icon: <Sword className="w-4 h-4 text-red-400" />,         color: 'red-400' },
    { key: 'RECOMMANDATIONS PRIORITAIRES', label: 'Recommandations prioritaires',          icon: <ShieldAlert className="w-4 h-4 text-brand-green" />, color: 'brand-green' },
  ];

  return (
    <div className="min-h-screen bg-brand-black pb-16">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-brand-black/95 backdrop-blur border-b border-gray-800 px-6 py-3">
        <div className="w-[82%] mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/ad')}
              className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <div className="w-px h-5 bg-gray-700" />
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <span className="text-white font-bold">Audit AD — {data.domain || '—'}</span>
              <span className="text-gray-500 text-sm ml-2 font-mono">{data.dc_ip}</span>
              <span className="text-xs text-gray-600 ml-2">· {scanId.slice(0, 8)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data.status === 'running' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border bg-blue-500/20 text-blue-400 border-blue-500/40 text-sm">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> En cours
              </span>
            )}
            {data.status === 'completed' && globalScore !== undefined && (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-bold ${rl.bg} ${rl.color}`}>
                Score {globalScore}/100 — {rl.label}
              </span>
            )}
            {data.status === 'failed' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border bg-red-500/20 text-red-400 border-red-500/40 text-sm">
                <XCircle className="w-3.5 h-3.5" /> Échoué
              </span>
            )}
            <button onClick={fetchScan}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Actualiser">
              <RefreshCw className={`w-4 h-4 ${polling ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="w-[82%] mx-auto pt-6 space-y-6">
        {/* Running banner */}
        {data.status === 'running' && (
          <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <Loader2 className="w-5 h-5 text-blue-400 animate-spin flex-shrink-0" />
            <div>
              <span className="text-sm font-semibold text-blue-400">Audit AD en cours</span>
              <p className="text-xs text-gray-400 mt-0.5">Analyse de l'Active Directory {data.domain}… La page se rafraîchit automatiquement.</p>
            </div>
          </div>
        )}

        {data.status === 'failed' && data.error && (
          <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-sm font-bold text-red-400">Audit échoué</span>
              <p className="text-xs text-gray-300 mt-0.5 font-mono">{data.error}</p>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={<AlertTriangle className="w-5 h-5 text-orange-400" />}
            label="Indicateurs de risque"
            value={indicators.length}
            sub={`${sevCounts.Critical} critique${sevCounts.Critical > 1 ? 's' : ''}, ${sevCounts.High} élevé${sevCounts.High > 1 ? 's' : ''}`}
            danger={sevCounts.Critical > 0}
          />
          <KpiCard
            icon={<Crown className="w-5 h-5 text-red-400" />}
            label="Domain Admins"
            value={stats.domain_admins}
            sub="Comptes à privilèges maximaux"
            danger={(stats.domain_admins || 0) > 5}
          />
          <KpiCard
            icon={<Key className="w-5 h-5 text-yellow-400" />}
            label="Kerberoastable"
            value={(stats.kerberoastable || 0) + (stats.asreproastable || 0)}
            sub={`${stats.kerberoastable || 0} SPN · ${stats.asreproastable || 0} AS-REP`}
            danger={(stats.kerberoastable || 0) + (stats.asreproastable || 0) > 0}
          />
          <KpiCard
            icon={<Users className="w-5 h-5 text-blue-400" />}
            label="Utilisateurs / Machines"
            value={`${stats.total_users || 0} / ${stats.total_computers || 0}`}
            sub={`${stats.stale_users || 0} util. inactifs · ${stats.stale_computers || 0} PC inactifs`}
            neutral
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-800">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? tab.highlight ? 'border-purple-500 text-purple-400' : 'border-purple-500 text-purple-400'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}>
              {tab.icon} {tab.label}
              {tab.badge && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab: Overview ── */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ScoreRing score={globalScore} />
            <CategoryScores scores={scores} byCategory={byCategory} />

            {/* Scan info */}
            <div className="bg-brand-black-light rounded-xl border border-gray-800 p-5 lg:col-span-2">
              <div className="text-sm font-semibold text-gray-400 mb-4">Informations de l'audit</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Domaine', value: data.domain || '—' },
                  { label: 'DC IP', value: data.dc_ip || '—', mono: true },
                  { label: 'Scan ID', value: scanId.slice(0, 16) + '…', mono: true },
                  { label: 'Statut', value: data.status === 'completed' ? '✓ Terminé' : data.status === 'running' ? '⟳ En cours' : '✗ Échoué',
                    className: data.status === 'completed' ? 'text-green-400' : data.status === 'running' ? 'text-blue-400' : 'text-red-400' },
                  { label: 'Démarré', value: data.started_at ? new Date(data.started_at).toLocaleString() : '—' },
                  { label: 'Terminé', value: data.completed_at ? new Date(data.completed_at).toLocaleString() : '—' },
                  { label: 'Indicateurs critiques', value: sevCounts.Critical,
                    className: sevCounts.Critical > 0 ? 'text-red-400 font-bold' : 'text-green-400' },
                  { label: 'Indicateurs élevés', value: sevCounts.High,
                    className: sevCounts.High > 0 ? 'text-orange-400 font-bold' : 'text-green-400' },
                ].map(({ label, value, mono, className }) => (
                  <div key={label}>
                    <div className="text-xs text-gray-500 mb-1">{label}</div>
                    <div className={`text-sm font-medium ${className || 'text-white'} ${mono ? 'font-mono' : ''}`}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top critical indicators */}
            {sortedIndicators.filter(i => i.severity === 'Critical').length > 0 && (
              <div className="bg-brand-black-light rounded-xl border border-red-500/20 p-5 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-semibold text-red-400">Indicateurs critiques à traiter en priorité</span>
                  </div>
                  <button onClick={() => setActiveTab('indicators')}
                    className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
                    Voir tous <ChevronDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="space-y-2">
                  {sortedIndicators.filter(i => i.severity === 'Critical').slice(0, 4).map((ind, i) => {
                    const sc = getSev(ind.severity);
                    return (
                      <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${sc.bg} ${sc.border}`}>
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sc.dot}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-white">{ind.title}</span>
                            {ind.matched_count > 0 && (
                              <span className="text-xs font-bold bg-gray-700 text-white px-1.5 py-0.5 rounded-full">
                                {ind.matched_count}
                              </span>
                            )}
                          </div>
                          {ind.solution && <p className="text-xs text-green-400/80 mt-0.5">{ind.solution}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* AI teaser */}
            <div
              onClick={() => setActiveTab('ai')}
              className="bg-gradient-to-r from-purple-500/5 to-red-500/5 rounded-xl border border-purple-500/20 p-5 lg:col-span-2 cursor-pointer hover:border-purple-500/40 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">Analyse IA — Guide de compromission AD</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      L'IA analyse les {indicators.length} indicateurs AD et génère les vecteurs d'attaque, commandes d'exploitation et remédiation.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-purple-400 group-hover:gap-3 transition-all">
                  <span className="text-sm font-medium">Générer</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Indicators ── */}
        {activeTab === 'indicators' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { id: 'all',      label: `Tous (${indicators.length})` },
                { id: 'Critical', label: `Critique (${sevCounts.Critical})`, color: 'text-red-400 border-red-500/30 bg-red-500/10' },
                { id: 'High',     label: `Élevé (${sevCounts.High})`,        color: 'text-orange-400 border-orange-500/30 bg-orange-500/10' },
                { id: 'Medium',   label: `Moyen (${sevCounts.Medium})`,      color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' },
                { id: 'Low',      label: `Faible (${sevCounts.Low})`,        color: 'text-blue-400 border-blue-500/30 bg-blue-500/10' },
              ].map(f => (
                <button key={f.id} onClick={() => setIndFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    indFilter === f.id
                      ? (f.color || 'bg-purple-500/20 text-purple-400 border-purple-500/30')
                      : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-white'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>

            {filteredIndicators.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-green-500/40 mx-auto mb-3" />
                <p className="text-gray-400 font-medium">Aucun indicateur</p>
                <p className="text-gray-600 text-sm mt-1">
                  {indFilter !== 'all' ? `Aucun indicateur de sévérité ${indFilter}.` : 'Aucun indicateur de risque détecté.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredIndicators.map((ind, i) => (
                  <IndicatorCard key={ind.id || i} indicator={ind} index={i} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: AD Statistics ── */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {/* Objets & comptes */}
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Inventaire</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Utilisateurs',       value: stats.total_users,     icon: <Users className="w-5 h-5 text-blue-400" />,   danger: false },
                  { label: 'Ordinateurs',         value: stats.total_computers, icon: <Server className="w-5 h-5 text-purple-400" />, danger: false },
                  { label: 'GPO',                 value: stats.gpos,            icon: <FileText className="w-5 h-5 text-blue-300" />, danger: false },
                  { label: 'Trusts de domaine',   value: stats.trusts,          icon: <GitBranch className="w-5 h-5 text-purple-400" />, danger: (stats.trusts || 0) > 0 },
                ].map(({ label, value, icon, danger }) => (
                  <div key={label} className={`bg-brand-black-light rounded-xl p-4 border ${danger && (value || 0) > 0 ? 'border-orange-500/30' : 'border-gray-800'} flex items-center gap-3`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${danger && (value || 0) > 0 ? 'bg-orange-500/10' : 'bg-gray-800'}`}>{icon}</div>
                    <div>
                      <div className={`text-2xl font-black ${danger && (value || 0) > 0 ? 'text-orange-400' : (value || 0) === 0 ? 'text-gray-600' : 'text-white'}`}>{value ?? '—'}</div>
                      <div className="text-xs text-gray-500">{label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Groupes privilégiés */}
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Groupes privilégiés</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Domain Admins',       value: stats.domain_admins,      icon: <Crown className="w-5 h-5 text-red-400" />,    danger: (stats.domain_admins || 0) > 5 },
                  { label: 'Enterprise Admins',   value: stats.enterprise_admins,  icon: <Crown className="w-5 h-5 text-orange-400" />, danger: (stats.enterprise_admins || 0) > 1 },
                  { label: 'Backup Operators',    value: stats.backup_operators,   icon: <Database className="w-5 h-5 text-yellow-400" />, danger: (stats.backup_operators || 0) > 0 },
                  { label: 'Account Operators',   value: stats.account_operators,  icon: <Users className="w-5 h-5 text-yellow-300" />, danger: (stats.account_operators || 0) > 0 },
                ].map(({ label, value, icon, danger }) => (
                  <div key={label} className={`bg-brand-black-light rounded-xl p-4 border ${danger && (value || 0) > 0 ? 'border-red-500/30' : 'border-gray-800'} flex items-center gap-3`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${danger && (value || 0) > 0 ? 'bg-red-500/10' : 'bg-gray-800'}`}>{icon}</div>
                    <div>
                      <div className={`text-2xl font-black ${danger && (value || 0) > 0 ? 'text-red-400' : (value || 0) === 0 ? 'text-gray-600' : 'text-white'}`}>{value ?? '—'}</div>
                      <div className="text-xs text-gray-500">{label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Kerberos & délégation */}
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Kerberos & Délégation</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Kerberoastable (SPN)',     value: stats.kerberoastable,           icon: <Key className="w-5 h-5 text-orange-400" />,    danger: (stats.kerberoastable || 0) > 0 },
                  { label: 'AS-REP Roastable',         value: stats.asreproastable,           icon: <Zap className="w-5 h-5 text-yellow-400" />,    danger: (stats.asreproastable || 0) > 0 },
                  { label: 'Délég. non contrainte',    value: stats.unconstrained_delegation, icon: <Network className="w-5 h-5 text-red-400" />,   danger: (stats.unconstrained_delegation || 0) > 0 },
                  { label: 'Délég. contrainte',        value: stats.constrained_delegation,   icon: <Network className="w-5 h-5 text-blue-400" />,  danger: (stats.constrained_delegation || 0) > 0 },
                ].map(({ label, value, icon, danger }) => (
                  <div key={label} className={`bg-brand-black-light rounded-xl p-4 border ${danger && (value || 0) > 0 ? 'border-orange-500/30' : 'border-gray-800'} flex items-center gap-3`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${danger && (value || 0) > 0 ? 'bg-orange-500/10' : 'bg-gray-800'}`}>{icon}</div>
                    <div>
                      <div className={`text-2xl font-black ${danger && (value || 0) > 0 ? 'text-orange-400' : (value || 0) === 0 ? 'text-gray-600' : 'text-white'}`}>{value ?? '—'}</div>
                      <div className="text-xs text-gray-500">{label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* LAPS & ADCS */}
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">LAPS & PKI (ADCS)</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className={`bg-brand-black-light rounded-xl p-4 border ${stats.laps_deployed ? 'border-green-500/30' : 'border-red-500/30'} flex items-center gap-3`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${stats.laps_deployed ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    <Shield className={`w-5 h-5 ${stats.laps_deployed ? 'text-green-400' : 'text-red-400'}`} />
                  </div>
                  <div>
                    <div className={`text-sm font-black ${stats.laps_deployed ? 'text-green-400' : 'text-red-400'}`}>{stats.laps_deployed ? 'Déployé' : 'Absent'}</div>
                    <div className="text-xs text-gray-500">LAPS</div>
                  </div>
                </div>
                {stats.laps_deployed && (
                  <div className="bg-brand-black-light rounded-xl p-4 border border-gray-800 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-800">
                      <Server className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <div className="text-2xl font-black text-white">{stats.laps_computers_covered ?? '—'}</div>
                      <div className="text-xs text-gray-500">Machines couvertes LAPS</div>
                    </div>
                  </div>
                )}
                {[
                  { label: 'Autorités de cert. (CA)', value: stats.adcs_cas,                 icon: <Star className="w-5 h-5 text-purple-400" />,  danger: false },
                  { label: 'Templates vulnérables',   value: stats.adcs_vulnerable_templates, icon: <AlertTriangle className="w-5 h-5 text-red-400" />, danger: (stats.adcs_vulnerable_templates || 0) > 0 },
                ].map(({ label, value, icon, danger }) => (
                  <div key={label} className={`bg-brand-black-light rounded-xl p-4 border ${danger && (value || 0) > 0 ? 'border-red-500/30' : 'border-gray-800'} flex items-center gap-3`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${danger && (value || 0) > 0 ? 'bg-red-500/10' : 'bg-gray-800'}`}>{icon}</div>
                    <div>
                      <div className={`text-2xl font-black ${danger && (value || 0) > 0 ? 'text-red-400' : (value || 0) === 0 ? 'text-gray-600' : 'text-white'}`}>{value ?? '—'}</div>
                      <div className="text-xs text-gray-500">{label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Autres anomalies */}
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Autres anomalies</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'MDP sans expiration',  value: stats.never_expires,   icon: <Lock className="w-5 h-5 text-gray-400" />,       danger: (stats.never_expires || 0) > 0 },
                  { label: 'SIDHistory',           value: stats.sid_history,     icon: <AlertTriangle className="w-5 h-5 text-orange-400" />, danger: (stats.sid_history || 0) > 0 },
                  { label: 'Ordinateurs inactifs', value: stats.stale_computers, icon: <AlertTriangle className="w-5 h-5 text-yellow-400" />, danger: (stats.stale_computers || 0) > 0 },
                  { label: 'Utilisateurs inactifs',value: stats.stale_users,     icon: <AlertTriangle className="w-5 h-5 text-yellow-300" />, danger: (stats.stale_users || 0) > 0 },
                ].map(({ label, value, icon, danger }) => (
                  <div key={label} className={`bg-brand-black-light rounded-xl p-4 border ${danger && (value || 0) > 0 ? 'border-orange-500/30' : 'border-gray-800'} flex items-center gap-3`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${danger && (value || 0) > 0 ? 'bg-orange-500/10' : 'bg-gray-800'}`}>{icon}</div>
                    <div>
                      <div className={`text-2xl font-black ${danger && (value || 0) > 0 ? 'text-orange-400' : (value || 0) === 0 ? 'text-gray-600' : 'text-white'}`}>{value ?? '—'}</div>
                      <div className="text-xs text-gray-500">{label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Interprétation */}
            <div className="bg-brand-black-light rounded-xl border border-gray-800 p-5">
              <div className="text-sm font-semibold text-gray-400 mb-4">Interprétation des statistiques</div>
              <div className="space-y-2 text-sm text-gray-300">
                {!stats.laps_deployed && (stats.total_computers || 0) > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-red-500/5 border border-red-500/20">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-red-400">LAPS absent</strong> — {stats.total_computers} postes partagent le même mot de passe administrateur local. Risque critique de lateral movement Pass-the-Hash.</span>
                  </div>
                )}
                {(stats.adcs_vulnerable_templates || 0) > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-red-500/5 border border-red-500/20">
                    <Star className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-red-400">{stats.adcs_vulnerable_templates} templates ADCS vulnérables</strong> — ESC1/ESC2/ESC3 permettent d'obtenir des certificats pour usurper un Domain Admin. Exploitable avec certipy.</span>
                  </div>
                )}
                {(stats.domain_admins || 0) > 5 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-red-500/5 border border-red-500/20">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-red-400">{stats.domain_admins} Domain Admins</strong> — Recommandé : ≤ 5. Chaque DA représente un vecteur d'escalade vers le contrôle du domaine.</span>
                  </div>
                )}
                {(stats.enterprise_admins || 0) > 1 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-red-500/5 border border-red-500/20">
                    <Crown className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-orange-400">{stats.enterprise_admins} Enterprise Admins</strong> — Ce groupe contrôle l'ensemble de la forêt. Il ne doit contenir que Administrator.</span>
                  </div>
                )}
                {(stats.backup_operators || 0) > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-orange-500/5 border border-orange-500/20">
                    <Database className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-orange-400">{stats.backup_operators} Backup Operators</strong> — Peut lire ntds.dit via VSS et dumper tous les hashes AD. Équivalent Domain Admin pour la compromission.</span>
                  </div>
                )}
                {(stats.kerberoastable || 0) > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-orange-500/5 border border-orange-500/20">
                    <Key className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-orange-400">{stats.kerberoastable} comptes Kerberoastable</strong> — Un attaquant authentifié peut demander des tickets TGS et les cracker offline (impacket GetUserSPNs).</span>
                  </div>
                )}
                {(stats.asreproastable || 0) > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-yellow-500/5 border border-yellow-500/20">
                    <Zap className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-yellow-400">{stats.asreproastable} AS-REP Roastable</strong> — Exploitable sans credentials : GetNPUsers.py -no-pass. Les hashes AS-REP sont crackables offline.</span>
                  </div>
                )}
                {(stats.unconstrained_delegation || 0) > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-red-500/5 border border-red-500/20">
                    <Network className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-red-400">{stats.unconstrained_delegation} délégation non contrainte</strong> — Permet d'usurper n'importe quel utilisateur s'authentifiant sur le service. Abus via Rubeus + PrinterBug.</span>
                  </div>
                )}
                {(stats.sid_history || 0) > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-orange-500/5 border border-orange-500/20">
                    <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-orange-400">{stats.sid_history} comptes avec SIDHistory</strong> — Peut permettre l'escalade de privilèges inter-domaines si un SID privilégié est présent.</span>
                  </div>
                )}
                {(stats.never_expires || 0) > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-gray-500/5 border border-gray-700">
                    <Lock className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-gray-300">{stats.never_expires} MDP sans expiration</strong> — Comptes dont le mot de passe ne sera jamais réinitialisé automatiquement.</span>
                  </div>
                )}
                {!stats.laps_deployed === false && (stats.adcs_vulnerable_templates || 0) === 0 && (stats.domain_admins || 0) <= 5 && (stats.kerberoastable || 0) === 0 && (stats.asreproastable || 0) === 0 && (stats.unconstrained_delegation || 0) === 0 && (
                  <div className="flex items-center gap-2 p-2 rounded bg-green-500/5 border border-green-500/20">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-green-300">Aucun indicateur statistique critique détecté.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Action Plan ── */}
        {activeTab === 'actions' && (
          <div className="space-y-4">
            <div className="bg-brand-black-light rounded-xl border border-gray-800 p-4 flex items-start gap-3">
              <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-400">
                Plan d'action priorisé basé sur les {indicators.length} indicateurs détectés.
                Les actions immédiates (&lt; 30j) doivent être traitées avant de générer les rapports AD.
              </p>
            </div>
            <ActionPlan indicators={sortedIndicators} />
          </div>
        )}

        {/* ── Tab: AI Analysis ── */}
        {activeTab === 'ai' && (
          <div className="space-y-6">
            {/* Sub-tabs (after generation) */}
            {aiAnalysis && (
              <div className="flex gap-2 border-b border-gray-800">
                {[
                  { id: 'analysis', label: 'Analyse & Guide d\'attaque', icon: <Brain className="w-3.5 h-3.5" /> },
                  { id: 'evidence', label: `Preuves & Captures (${indicators.length})`, icon: <Camera className="w-3.5 h-3.5" /> },
                ].map(st => (
                  <button key={st.id} onClick={() => setAiSubTab(st.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-t-lg border-b-2 transition-colors ${
                      aiSubTab === st.id ? 'border-purple-500 text-purple-400' : 'border-transparent text-gray-400 hover:text-white'
                    }`}>
                    {st.icon} {st.label}
                  </button>
                ))}
              </div>
            )}

            {/* Generate button */}
            {!aiAnalysis && !loadingAI && (
              <div className="bg-brand-black-light rounded-xl border border-purple-500/20 p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                  <Brain className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Analyse IA — Guide de compromission AD</h3>
                <p className="text-gray-400 text-sm mb-2 max-w-xl mx-auto">
                  L'IA va analyser les {indicators.length} indicateurs AD pour :
                </p>
                <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto mb-6 text-left">
                  {[
                    { icon: <Lightbulb className="w-4 h-4 text-yellow-400" />, text: 'Expliquer les scores et indicateurs' },
                    { icon: <Crosshair className="w-4 h-4 text-orange-400" />, text: 'Kerberoast, AS-REP, délégation…' },
                    { icon: <Sword className="w-4 h-4 text-red-400" />, text: 'Guide étape par étape vers DA' },
                    { icon: <ShieldAlert className="w-4 h-4 text-brand-green" />, text: 'Remédiation PowerShell/AD' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-2.5 bg-gray-900 rounded-lg border border-gray-800">
                      {item.icon}
                      <span className="text-xs text-gray-300">{item.text}</span>
                    </div>
                  ))}
                </div>
                {aiError && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                    {aiError}
                  </div>
                )}
                <button onClick={generateAIAnalysis}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-500/20 text-purple-400 rounded-xl hover:bg-purple-500/30 border border-purple-500/30 transition-all font-semibold mx-auto">
                  <Sparkles className="w-4 h-4" /> Générer l'analyse IA
                </button>
                <p className="text-xs text-gray-600 mt-3">Génération via Ollama (qwen2.5-coder:32b) — environ 2-4 minutes</p>
              </div>
            )}

            {/* Loading */}
            {loadingAI && (
              <div className="bg-brand-black-light rounded-xl border border-purple-500/20 p-8 text-center">
                <div className="relative w-16 h-16 mx-auto mb-4">
                  <div className="w-16 h-16 rounded-full border-2 border-purple-500/30 animate-ping absolute inset-0" />
                  <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Brain className="w-7 h-7 text-purple-400 animate-pulse" />
                  </div>
                </div>
                <p className="text-white font-semibold mb-1">Analyse en cours…</p>
                <p className="text-gray-400 text-sm">L'IA analyse les {indicators.length} indicateur(s) AD et génère le guide d'attaque</p>
                <p className="text-gray-600 text-xs mt-2">Cela peut prendre 2 à 4 minutes selon la charge des serveurs GPU</p>
                <div className="mt-4 flex justify-center gap-1">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}

            {/* Analysis results */}
            {aiAnalysis && aiSubTab === 'analysis' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-purple-500/5 rounded-xl border border-purple-500/20">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    <div>
                      <span className="text-sm font-semibold text-white">
                        Analyse générée — {aiAnalysis.domain} · Score {aiAnalysis.global_score ?? '?'}/100 ({aiAnalysis.risk_level})
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {aiAnalysis.indicators_count} indicateurs analysés
                      </p>
                    </div>
                  </div>
                  <button onClick={generateAIAnalysis}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-xs hover:bg-purple-500/20 border border-purple-500/30 transition-colors">
                    <RefreshCw className="w-3 h-3" /> Regénérer
                  </button>
                </div>

                {sectionDefs.map(def => (
                  <AISectionBlock
                    key={def.key}
                    title={def.label}
                    icon={def.icon}
                    content={aiSections[def.key] || ''}
                    color={def.color}
                    collapsible={true}
                  />
                ))}

                {Object.keys(aiSections).length === 0 && aiAnalysis.raw && (
                  <div className="bg-brand-black-light rounded-xl border border-gray-700 p-5">
                    <div className="text-sm font-semibold text-gray-400 mb-3">Réponse brute</div>
                    <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{aiAnalysis.raw}</div>
                  </div>
                )}

                {aiAnalysis.error && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-400">
                    Note : L'analyse a utilisé un fallback (service IA indisponible). {aiAnalysis.error}
                  </div>
                )}
              </div>
            )}

            {/* Evidence / captures */}
            {aiAnalysis && aiSubTab === 'evidence' && (
              <IndicatorCaptures
                indicators={sortedIndicators}
                domain={data.domain || ''}
                dc_ip={data.dc_ip || ''}
                scores={scores}
                stats={stats}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PingCastleDetail;
